/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2020 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import * as R from 'ramda';
import classnames from 'classnames';
import React, { useState } from 'react';
import Copy from '@nebula/luna/dist/core/copy';
import { defineMessages, injectIntl } from '@nebula/izzy/intl';
import { Accordion, AccordionItem } from '@react/react-spectrum/Accordion';
import Checkbox from '@react/react-spectrum/Checkbox';
import Heading from '@react/react-spectrum/Heading';
import Select from '@react/react-spectrum/Select';
import { Tab, TabList } from '@react/react-spectrum/TabList';
import Textarea from '@react/react-spectrum/Textarea';
import styles from './index.styl';

const MSG = defineMessages({
  orgConfig: {
    id: 'gemini.settings.pluginview.orgtab',
    defaultMessage: 'For Your Company'
  },
  userConfig: {
    id: 'gemini.settings.pluginview.usertab',
    defaultMessage: 'For You'
  },
  sessionConfig: {
    id: 'gemini.settings.pluginview.sessionTab',
    defaultMessage: 'For this Session'
  },
  advancedOptions: {
    id: 'gemini.settings.plugin.validation.advancedOptions',
    defaultMessage: 'Advanced Options'
  },
  addArgumentsInfo: {
    id: 'gemini.settings.plugin.validation.addArgumentsInfo',
    defaultMessage: 'Enter a semicolon delimited list of arguments. The plugin will be executed for each item.'
  },
  descriptionNotAvailable: {
    id: 'gemini.settings.plugin.validation.descriptionNotAvailable',
    defaultMessage: 'No description provided.'
  },
  levelOff: {
    id: 'gemini.settings.plugin.validation.level.off',
    defaultMessage: 'Off'
  },
  levelWarn: {
    id: 'gemini.settings.plugin.validation.level.warn',
    defaultMessage: 'Warn'
  },
  levelError: {
    id: 'gemini.settings.plugin.validation.level.error',
    defaultMessage: 'Error'
  },
  showAdvancedOptions: {
    id: 'gemini.settings.plugin.validation.showAdvancedOptions',
    defaultMessage: 'Show Advanced Options'
  }
});

const LEVEL_MESSAGES = {
  error: 'levelError',
  warn: 'levelWarn',
  off: 'levelOff'
};

const modes = ['org', 'userorg', 'session'];

const ManageValidationPlugins = ({
  enabledViewValidators,
  izzy,
  mode = 'org',
  onMode,
  onChangeArguments,
  onSelectLevel,
  plugins,
  settings,
  viewValidators
}) => {
  const [showAdvancedOptions, setShowAdvancedOptions] = useState({});
  const defaultSelectedIndex = R.addIndex(R.map)((val, idx) => idx, R.keys(plugins));
  const levelOptions = R.pipe(
    R.keys,
    R.map(key => ({ label: izzy(MSG[LEVEL_MESSAGES[key]]), value: key }))
  )(LEVEL_MESSAGES);

  return (
    <div
      className={styles.root}
    >
      <TabList
        selectedIndex={modes.indexOf(mode)}
        onChange={i => onMode(modes[i])}
        quiet
      >
        <Tab>
          {izzy(MSG.orgConfig)}
        </Tab>
        <Tab>
          {izzy(MSG.userConfig)}
        </Tab>
        <Tab>
          {izzy(MSG.sessionConfig)}
        </Tab>
      </TabList>
      <div
        className={classnames(
          styles.root,
          (!plugins || !plugins.length) && styles.noViewPlugins
        )}
      >
        {R.map((categoryPlugins) => {
          const category = R.path([0, 'category'], categoryPlugins);

          return (
            <Accordion
              defaultSelectedIndex={defaultSelectedIndex}
              key={category}
              multiselectable
            >
              <AccordionItem header={category}>
                {R.map(({ description, displayName, namespace }) => {
                  const validationArgs = R.pathOr('', [namespace, 'arguments'], settings);
                  const validationLevel = R.path([namespace, 'level'], settings);

                  return (
                    <div className={styles.validation} key={namespace}>
                      <div className={styles.basic}>
                        <div className={styles.textRow}>
                          <Heading variant="subtitle2">{displayName}</Heading>
                          <Copy>{description || izzy(MSG.descriptionNotAvailable)}</Copy>
                        </div>
                        <div>
                          {viewValidators.indexOf(namespace) === -1 && (
                            <Checkbox
                              checked={showAdvancedOptions[namespace] || false}
                              label={izzy(MSG.showAdvancedOptions)}
                              onChange={v => setShowAdvancedOptions(
                                R.assoc(namespace, v, showAdvancedOptions)
                              )}
                            />
                          )}
                          <Select
                            flexible
                            variant="action"
                            onSelect={v => onSelectLevel(namespace, v)}
                            options={levelOptions}
                            value={validationLevel || (enabledViewValidators.indexOf(namespace) > -1 ? 'error' : 'off')}
                          />
                        </div>
                      </div>
                      {showAdvancedOptions[namespace] && (
                        <div className={styles.advanced}>
                          <Copy>
                            {izzy(MSG.addArgumentsInfo)}
                          </Copy>
                          <Textarea
                            disabled={validationLevel === 'off'}
                            onChange={v => onChangeArguments(namespace, v)}
                            value={validationArgs}
                          />
                        </div>
                      )}
                    </div>
                  );
                }, categoryPlugins)}
              </AccordionItem>
            </Accordion>
          );
        }, R.values(plugins))}
      </div>
    </div>
  );
};

ManageValidationPlugins.displayName = 'ManageValidationPlugins';
export default injectIntl(ManageValidationPlugins);
